import type { CSSProperties, JSX } from 'react';
import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { PortalHeader } from './PortalHeader';
import { usePasskeyAutofill } from './usePasskeyAutofill';
import { fieldProps } from './fieldProps';
import {
  DEFAULT_LOGIN_COPY,
  fetchDiscoverOptions,
  submitEmail,
  submitLoginCode,
  verifyDiscover,
  type LoginResult,
  type LoginScreenCopy,
  type LoginScreenEndpoints,
  type LoginSetters,
} from './loginFlow';

export type {
  LoginResult,
  LoginScreenCopy,
  LoginScreenEndpoints,
  PasskeyOutcome,
} from './loginFlow';

/**
 * The estate's ONE sign-in screen (ADR-018 / AUTH-8) — so no portal ever
 * hand-builds a login again.
 *
 * WHY self-contained (no router): a single component owns BOTH the "Sign In"
 * (email) step and the "Enter Login Code" step through internal `step` state.
 * That lets an adopting portal delete not just its `LoginPage` but its separate
 * routed `VerifyPage` clone too — the whole two-screen ceremony collapses to one
 * `<LoginScreen>` mount. The stateful flow lives in `loginFlow.ts` (framework-
 * free, unit-tested); this component just owns the state cells and paints.
 *
 * COLOUR INVARIANT (registrar requirement, UX-1 "one signature colour per
 * portal"): `accentColor` styles the WHOLE screen (header strip, primary button,
 * links, focus rings) via the @cca/ui single-spoke token system — and it MUST be
 * the SAME colour the portal passes to `renderOtpCodeEmail({ accentColor })` in
 * @cca/email. The screen and the login-code email are two faces of one door; if
 * they drift, the person who just saw an emerald screen gets a gold email. The
 * email side is the portal's server wiring (already supported by @cca/email);
 * this component's job is to expose + apply the colour so the two match.
 *
 * Styling depends on the shared classes in `@cca/ui/components` (`.btn`,
 * `.input`, `.label`) and the role tokens from `@cca/ui/styles` — an adopter
 * imports both, exactly as the reference finance/vetting doors do.
 */
export type LoginMode = 'passkey-first' | 'otp-only';

export interface LoginScreenProps {
  /** Portal identity for the header + copy (e.g. "Vetting Portal"). */
  portalName: string;
  /**
   * The portal's signature colour (UX-1). Drives the entire screen AND must
   * equal the `accentColor` handed to `renderOtpCodeEmail` — one colour, both
   * faces of the door. See the component's colour invariant above.
   */
  accentColor: string;
  /** Full URL paths for every route (portals differ; nothing is hardcoded). */
  endpoints: LoginScreenEndpoints;
  /**
   * `passkey-first` (default) shows the one-tap passkey button + arms conditional
   * UI; `otp-only` (quota — AUTH-9 severance) renders NO passkey button and makes
   * ZERO passkey calls: email → code only.
   */
  mode?: LoginMode;
  /** Called with the verify body on success — the portal persists its session
   *  (cookie portals ignore the body; token portals read `token`) + enters. */
  onSignedIn: (result: LoginResult) => void;
  /** The 403 message (e.g. vetting's not-an-approver copy); a generic default
   *  is used otherwise. Applied to both the passkey and OTP authz-denied paths. */
  authzDeniedCopy?: string;
  /** Optional wording overrides; each defaults to AUTH-8's exact string. */
  copy?: LoginScreenCopy;
  /**
   * The fetch credentials mode. Defaults to `same-origin` — what the reference
   * cookie (vetting) and bearer (finance) LoginPages use (they omit the option,
   * which is `same-origin`). Override only for a cross-origin door.
   */
  credentials?: RequestCredentials;
  /** Optional footer line under the form; defaults to the portal name + year. */
  footer?: string;
}

const COLUMN: CSSProperties = { maxWidth: 384, margin: '0 auto' };

const HEADING: CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  color: 'var(--color-ink, #111827)',
  marginBottom: 8,
};
const SUBTITLE: CSSProperties = {
  fontSize: 14,
  color: 'var(--color-muted, #6b7280)',
  marginBottom: 32,
};
const FORM: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 24 };
const FOOTNOTE: CSSProperties = {
  marginTop: 24,
  fontSize: 12,
  color: 'var(--color-muted, #6b7280)',
  textAlign: 'center',
};
const FULL_BTN: CSSProperties = { width: '100%', justifyContent: 'center' };

const ERROR_BOX: CSSProperties = {
  border: '1px solid var(--color-danger, #c62828)',
  background: 'var(--color-danger-wash, #ffebee)',
  borderRadius: 8,
  padding: '12px 16px',
  fontSize: 13,
  color: 'var(--color-danger, #c62828)',
};
const INFO_BOX: CSSProperties = {
  border: '1px solid var(--color-info, #005487)',
  background: 'var(--color-info-wash, #e5eef4)',
  borderRadius: 8,
  padding: '12px 16px',
  fontSize: 13,
  color: 'var(--color-info, #005487)',
};

/** Step 1 — "Sign In": prominent passkey button (passkey-first) + a demoted
 *  email path. Exported for render tests; consumers use `<LoginScreen>`. */
export function EmailStep(props: {
  copy: Required<LoginScreenCopy>;
  showPasskey: boolean;
  email: string;
  onEmailChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onPasskeyClick: () => void;
  loading: boolean;
  passkeyBusy: boolean;
  otpFallback: boolean;
  error: string | null;
  info: string | null;
}): JSX.Element {
  const { copy, showPasskey, otpFallback, loading, passkeyBusy } = props;
  // Email path is demoted (ghost) when the passkey button is the fast path;
  // it becomes the primary CTA in otp-only mode or after a passkey fallback.
  const emailIsPrimary = !showPasskey || otpFallback;
  const submitLabel = loading
    ? emailIsPrimary
      ? copy.sendingButton
      : copy.signingInButton
    : emailIsPrimary
      ? copy.sendCodeButton
      : copy.continueWithEmail;

  return (
    <>
      <h2 style={HEADING}>{copy.signInHeading}</h2>
      <p style={SUBTITLE}>{copy.signInSubtitle}</p>

      {/* Passkey is the fast path — a true one-tap button, no email needed. */}
      {showPasskey && (
        <button
          type="button"
          onClick={props.onPasskeyClick}
          disabled={loading || passkeyBusy}
          className="btn btn-primary"
          style={{ ...FULL_BTN, marginBottom: 20 }}
        >
          {passkeyBusy ? copy.passkeyButtonBusy : copy.passkeyButton}
        </button>
      )}

      <form onSubmit={props.onSubmit} style={FORM}>
        <div>
          <label htmlFor="login-email" className="label">
            {copy.emailLabel}
          </label>
          <input
            id="login-email"
            className="input"
            type="email"
            value={props.email}
            onChange={(event) => props.onEmailChange(event.target.value)}
            placeholder={copy.emailPlaceholder}
            // `webauthn` arms the conditional-UI passkey dropdown; drop it in
            // otp-only mode, where there is no passkey to offer.
            autoComplete={showPasskey ? 'username webauthn' : 'username'}
            required
            disabled={loading}
          />
        </div>

        {/* WCAG 2.2 AA SC 4.1.3 (Status Messages): `role="alert"` (implicit
            assertive live region) makes AT announce a wrong code / rate-limit /
            authz-denial the moment it appears; `role="status"` + polite for the
            non-urgent "code sent" info. Attributes only — styling unchanged. */}
        {props.error && (
          <div role="alert" style={ERROR_BOX}>
            {props.error}
          </div>
        )}
        {props.info && (
          <div role="status" aria-live="polite" style={INFO_BOX}>
            {props.info}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || passkeyBusy || !props.email.trim()}
          className={`btn ${emailIsPrimary ? 'btn-primary' : 'btn-ghost'}`}
          style={FULL_BTN}
        >
          {submitLabel}
        </button>
      </form>

      <p style={FOOTNOTE}>{copy.emailFooter}</p>
    </>
  );
}

/** Step 2 — "Enter Login Code": centred 6-digit input with SMS/email autofill
 *  (MOB-3 `fieldProps('otp')`) + a ghost "Back to Email". Exported for tests. */
export function CodeStep(props: {
  copy: Required<LoginScreenCopy>;
  email: string;
  code: string;
  onCodeChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}): JSX.Element {
  const { copy, loading } = props;
  return (
    <>
      <h2 style={HEADING}>{copy.codeHeading}</h2>
      <p style={SUBTITLE}>
        {copy.codeSubtitlePrefix}
        <strong>{props.email}</strong>
      </p>

      <form onSubmit={props.onSubmit} style={FORM}>
        <div>
          <label htmlFor="login-code" className="label">
            {copy.codeLabel}
          </label>
          <input
            id="login-code"
            className="input"
            {...fieldProps('otp')}
            value={props.code}
            // Digits only, capped at 6 — matches the reference verify screens.
            onChange={(event) =>
              props.onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder="000000"
            maxLength={6}
            required
            disabled={loading}
            // UX-4 sanctions autofocus AFTER an explicit user action: the person
            // just submitted their email, so this step only exists on their
            // request. Moving focus here announces the step change to AT and
            // lands the MOB-3 SMS/email one-time-code autofill in a focused field.
            // eslint-disable-next-line jsx-a11y/no-autofocus -- UX-4: focus follows the user's email submit, not a first-load steal
            autoFocus
            style={{ textAlign: 'center', fontSize: 24, letterSpacing: '0.3em' }}
          />
        </div>

        {/* WCAG 2.2 AA SC 4.1.3: `role="alert"` so an invalid-code / rate-limit /
            authz-denial on the code step is announced by AT. Styling unchanged. */}
        {props.error && (
          <div role="alert" style={ERROR_BOX}>
            {props.error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || props.code.length !== 6}
          className="btn btn-primary"
          style={FULL_BTN}
        >
          {loading ? copy.verifyingButton : copy.verifyButton}
        </button>
        <button
          type="button"
          onClick={props.onBack}
          disabled={loading}
          className="btn btn-ghost"
          style={FULL_BTN}
        >
          {copy.backToEmail}
        </button>
      </form>

      <p style={FOOTNOTE}>{copy.codeFooter}</p>
    </>
  );
}

export function LoginScreen(props: LoginScreenProps): JSX.Element {
  const {
    portalName,
    accentColor,
    endpoints,
    mode = 'passkey-first',
    onSignedIn,
    authzDeniedCopy,
    credentials = 'same-origin',
    footer,
  } = props;
  const copy: Required<LoginScreenCopy> = { ...DEFAULT_LOGIN_COPY, ...props.copy };
  const authzDeniedText = authzDeniedCopy ?? copy.authzDenied;

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Once a passkey attempt is dismissed/fails, the submit becomes a plain
  // "email me a code" — no surprise second Touch ID prompt.
  const [otpFallback, setOtpFallback] = useState(false);
  const setters: LoginSetters = { setLoading, setError, setInfo, setOtpFallback, setStep };

  const passkeyEnabled = mode === 'passkey-first';

  // Usernameless passkey — conditional UI (autofill) armed on the email step +
  // the one-tap button. Resolves the account from the credential; no email
  // needed. Disarms on the code step and in otp-only mode.
  const passkey = usePasskeyAutofill<LoginResult>({
    enabled: passkeyEnabled && step === 'email',
    getOptions: () => fetchDiscoverOptions(endpoints, credentials),
    verify: (assertion) => verifyDiscover(endpoints, assertion, credentials),
    onSuccess: onSignedIn,
  });

  const showPasskey = passkeyEnabled && passkey.supported;

  const handleEmailSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    void submitEmail({
      email,
      usePasskey: showPasskey && !otpFallback,
      endpoints,
      copy,
      authzDeniedCopy: authzDeniedText,
      authenticate: (options) => startAuthentication({ optionsJSON: options }),
      onSignedIn,
      setters,
      credentials,
    });
  };

  const handleCodeSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    void submitLoginCode({
      email,
      code,
      endpoints,
      copy,
      authzDeniedCopy: authzDeniedText,
      onSignedIn,
      setters,
      credentials,
    });
  };

  /** Return to the email step, clearing the entered code + any error. */
  const backToEmail = (): void => {
    setStep('email');
    setCode('');
    setError(null);
  };

  // Single-spoke theme seed: set the one identity colour + derive the tonal
  // roles via color-mix, exactly as themes/portal.css does — so the whole screen
  // (header, primary button, links, focus) renders in accentColor with no theme
  // required, and `--brand` keeps the legacy PortalHeader fallback in step.
  const rootStyle: CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--color-surface, #f6f3ec)',
    ['--color-structural' as string]: accentColor,
    ['--color-structural-dk' as string]: 'color-mix(in srgb, var(--color-structural) 78%, black)',
    ['--color-structural-wash' as string]: 'color-mix(in srgb, var(--color-structural) 12%, white)',
    ['--color-accent' as string]: 'var(--color-structural)',
    ['--color-accent-wash' as string]: 'var(--color-structural-wash)',
    ['--color-link' as string]: 'var(--color-structural-dk)',
    ['--color-primary' as string]: 'var(--color-structural)',
    ['--color-primary-dk' as string]: 'var(--color-structural-dk)',
    // The primary fill IS the spoke, so its label is the strip's white — never a
    // night theme's dark --color-on-primary (Finance at night: 2.25:1). cca-x53l.
    ['--color-on-primary' as string]: 'var(--color-on-strip, #fff)',
    ['--brand' as string]: accentColor,
  };

  return (
    <div style={rootStyle}>
      <PortalHeader portalName={portalName} />
      <div
        style={{ flex: 1, maxWidth: 768, margin: '0 auto', width: '100%', padding: '40px 24px' }}
      >
        <div style={COLUMN}>
          {step === 'email' ? (
            <EmailStep
              copy={copy}
              showPasskey={showPasskey}
              email={email}
              onEmailChange={setEmail}
              onSubmit={handleEmailSubmit}
              onPasskeyClick={() => void passkey.signInWithPasskey()}
              loading={loading}
              passkeyBusy={passkey.busy}
              otpFallback={otpFallback}
              error={error ?? passkey.error}
              info={info}
            />
          ) : (
            <CodeStep
              copy={copy}
              email={email}
              code={code}
              onCodeChange={setCode}
              onSubmit={handleCodeSubmit}
              onBack={backToEmail}
              loading={loading}
              error={error}
            />
          )}
        </div>
      </div>
      <footer
        style={{
          borderTop: '1px solid var(--color-border, #e5e0d5)',
          background: 'var(--color-panel, #fff)',
          padding: '14px 0',
          textAlign: 'center',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--color-muted, #6b7280)',
        }}
      >
        {footer ?? `CCAANZ ${portalName} · ${new Date().getFullYear()}`}
      </footer>
    </div>
  );
}
